import { Text } from "@solomon-labs/design/components/ui";
import React from "react";

import { IndicationUnit } from "../../../components/Metrics/IndicationUnit";

import { TableRow } from "./TableRow";

/**
 * Eligibility of a qualified program participant. Mirrors the app's
 * `agentsMocks` union; kept here as a self-contained type so the hoisted
 * `ParticipantRow` / `QualifiedUsers` Views need no app import.
 */
export type EligibilityStatus =
    | "threshold_met"
    | "below_threshold"
    | "not_enrolled";

/**
 * A qualified program participant, normalized across the agent and holder
 * domains so a single row DC can render either. The app's adapters
 * (`agentToParticipant` / `holderToParticipant`) produce this shape; the sim
 * seeds it from fixtures.
 */
export interface Participant {
    id: string;
    /** Display name (truncates in the row). */
    name: string;
    /** Drives the leading status dot's state. */
    eligibility: EligibilityStatus;
    /** USDv balance held. */
    balance: number;
    /** USDv earned this period. */
    earned: number;
    /** USDv accrued toward the next distribution. */
    pending: number;
    /** Navigate to this participant's detail. */
    onNavigate: () => void;
}

/** Eligibility → IndicationUnit state (mirrors the Agents / Holders pages). */
const ELIGIBILITY_STATE: Record<
    EligibilityStatus,
    "success" | "pending" | "disabled"
> = {
    threshold_met: "success",
    below_threshold: "pending",
    not_enrolled: "disabled"
};

/** IndicationUnit state → human status label shown beside the dot. */
const STATUS_LABEL: Record<"success" | "pending" | "disabled", string> = {
    success: "Active",
    pending: "Pending",
    disabled: "Disabled"
};

/** Status-label text color — matches the dot's tone (same state → same color)
 *  so the dot + label read as one unit. Mirrors the Positions table. */
const STATUS_TEXT_COLOR: Record<"success" | "pending" | "disabled", string> = {
    success: "text-success-base",
    pending: "text-warning-base",
    disabled: "text-disabled-base"
};

/**
 * First-name-only form of a participant's display name, for the narrowest
 * container width. Fixture names carry an address prefix (`0x8f…4a2c · Maria
 * Santos`), so drop everything up to and including the ` · ` separator to
 * reach the human name, then take its first whitespace-delimited token
 * ("Maria"). Falls back to the whole string when neither delimiter is present.
 */
function firstName(name: string): string {
    const human = name.includes(" · ")
        ? name.slice(name.lastIndexOf(" · ") + 3)
        : name;
    return human.split(" ")[0] || human;
}

/** `$`-format a number with 2 fraction digits (grouped thousands). */
function formatUsd(n: number): string {
    return `$${n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
}

const CELL = "text-foreground-base font-numeric";

export interface ParticipantRowProps {
    participant: Participant;
    /** Sim-only cursor target key placed on the row. Inert in production. */
    cursorKey?: string;
    /** Sim-only — force the row's HOVER visual on with no pointer present, for
     *  scripted cursor tours. Default `false`. */
    forceHover?: boolean;
}

/**
 * ParticipantRow — one qualified-user row in the Qualified Users table. A
 * Domain Component that maps a normalized `Participant` to the `Table`'s
 * cells (keyed by column key): `status` = a status dot (`IndicationUnit`) +
 * label; `name` = the (truncating) user name; `balance` / `earned` /
 * `pending` = `$`-formatted numbers.
 * The whole row is the click target (`onNavigate` → the participant detail).
 *
 * Unifies the former `AgentLinkRow` + `HolderLinkRow` (~95% identical raw
 * `<button>` rows) into one DC over the `TableRow` GC — the two entity types
 * differ only in how their `Participant` is derived, so the shape is shared
 * and the divergence lives in the adapters (app-side), not in the row.
 *
 * Hoisted into `shared/widgets/yaas/programs` (from the app's
 * `pages/Programs/ParticipantRow`) so the shared `QualifiedUsers` View can
 * render fixture-driven in the "+New Program" wiki doc block. The app keeps a
 * re-export shim at the old path so `yaas-client` compiles unchanged.
 *
 * Composes GCs only (`TableRow`, `IndicationUnit`, `Text`) — imports no other
 * DC (dependency firewall).
 */
export function ParticipantRow({
    participant,
    cursorKey,
    forceHover = false
}: ParticipantRowProps): React.ReactElement {
    return (
        <TableRow
            onClick={participant.onNavigate}
            ariaLabel={`View ${participant.name}`}
            data-sim-cursor-key={cursorKey}
            forceHover={forceHover}
            cells={{
                status: (
                    <div className="flex items-center gap-2 min-w-0">
                        {/* Dot always; the label (dot's tone) drops below
                            `@4xl/qutable` — back to a bare dot, in lockstep with
                            the grid's Status track shrinking 88px→16px. This is
                            the FIRST thing to collapse as the table narrows. */}
                        <IndicationUnit
                            variant="status"
                            state={ELIGIBILITY_STATE[participant.eligibility]}
                            size={6}
                        />
                        <Text
                            variant="small"
                            render={<span />}
                            className={`hidden @4xl/qutable:inline truncate ${
                                STATUS_TEXT_COLOR[
                                    ELIGIBILITY_STATE[participant.eligibility]
                                ]
                            }`}
                        >
                            {
                                STATUS_LABEL[
                                    ELIGIBILITY_STATE[participant.eligibility]
                                ]
                            }
                        </Text>
                    </div>
                ),
                name: (
                    <div className="min-w-0">
                        {/* Name: full at `@lg/qutable`+; first-name only below
                            (LAST thing to drop). Two spans toggled in lockstep
                            so exactly one shows at any width. */}
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-base hidden @lg/qutable:block truncate"
                        >
                            {participant.name}
                        </Text>
                        <Text
                            variant="small"
                            render={<span />}
                            className="text-foreground-base block @lg/qutable:hidden truncate"
                        >
                            {firstName(participant.name)}
                        </Text>
                    </div>
                ),
                balance: (
                    <Text variant="small" render={<span />} className={CELL}>
                        {formatUsd(participant.balance)}
                    </Text>
                ),
                earned: (
                    <Text variant="small" render={<span />} className={CELL}>
                        {formatUsd(participant.earned)}
                    </Text>
                ),
                pending: (
                    <Text variant="small" render={<span />} className={CELL}>
                        {formatUsd(participant.pending)}
                    </Text>
                )
            }}
        />
    );
}
